// ---------------------------------------------------------------------------
// Pricing.js  —  /pricing
// ---------------------------------------------------------------------------
// Two-tier pricing page that positions K9os as a custom-built solution:
//
//   • Trainer Starter — $1,000+ setup + $125/mo. Website + board-and-train
//     flow + backend dashboard. For small operations that don't need
//     kennel/boarding scheduling or a POS system.
//
//   • Professional Full-Suite — $2,500+ setup + $250/mo. All features
//     including board-and-train, boarding + kennels, POS, memberships,
//     digital contracts, marketing, etc.
//
// Both tiers include 24/7 support, minor updates, and each trainer gets
// their OWN custom-built K9os app connected to their own Stripe/PayPal
// accounts + any integrations they need.
// ---------------------------------------------------------------------------
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, ChevronRight, Award, Sparkles, Zap, Building2, Users, MessageCircle,
  HeadphonesIcon, ShieldCheck, Rocket, CreditCard, Lock, Monitor,
} from 'lucide-react';
import { PublicNav } from '../components/PublicNav';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { DemoLeadSection } from '../components/DemoLeadSection';
import { PayPalMark } from '../components/PayPalMark';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

// Three tiers — the source of truth for pricing shown on this page AND on the
// homepage teaser. Update this array + re-import to keep both in sync.
export const PRICING_TIERS = [
  {
    slug: 'website',
    name: 'Custom Created Professional Dog Business Website',
    tagline: 'Your own dog-business React app website \u2014 the front door to K9os',
    setupPrice: '$500',
    setupSuffix: 'one-time set-up fee \u2014 you own the site outright',
    // Websites don't recur on their own — but every visitor bumps up to
    // adding the Dog Trainers plan below for the full backend experience.
    monthlyPrice: null,
    monthlyLabel: '',
    paypalPlanId: null,
    setupNote: 'One-time cost \u2014 no recurring fee unless you add the Dog Trainers plan below ($125/mo).',
    monthlyNote: null,
    icon: Monitor,
    badge: 'Start here',
    accent: 'from-[#0F1A33] to-[#1B3A87]',
    idealFor: 'Dog trainers who need a beautiful, SEO-ready website first \u2014 with the option to plug in the full K9os backend (bookings, CRM, POS, memberships, messaging) later by adding the $125/mo Dog Trainers plan.',
    includes: [
      'Custom React app website designed around your brand',
      'Mobile-responsive on every device \u2014 phones, tablets, laptops',
      'On your own domain (or one we help you pick + register)',
      'Photo gallery + success stories section for social proof',
      'You own the site \u2014 no hidden lock-in',
      'Optional: bolt on the Dog Trainers plan below (+$125/mo) to unlock bookings, CRM, POS, memberships, messaging, contracts, marketing, and 24/7 support',
    ],
    upgrades: [
      {
        label: 'SEO + keyword optimization',
        detail: 'Get indexed on Google, Bing, and AI-answer engines so ideal clients find your K9os site before they find your competitors.',
      },
    ],
    excluded: [
      'Backend admin dashboard (comes with $125/mo add-on)',
      'Client / dog CRM (comes with $125/mo add-on)',
      'Online bookings + payment processing (comes with $125/mo add-on)',
      'Client PWA + push notifications (comes with $125/mo add-on)',
    ],
    cta: 'Start with a Website',
  },
  {
    slug: 'trainer',
    name: 'Dog Trainers',
    tagline: 'Perfect for solo trainers & small board-and-train operations',
    setupPrice: '$1,000',
    setupSuffix: 'one-time branded set-up fee included*',
    monthlyPrice: '$125',
    monthlyLabel: '/ month',
    // Live PayPal Subscription plan ID for $125/month recurring
    paypalPlanId: 'P-01U5584474430552LNJ4L6IY',
    setupNote: 'Final quote depends on your specific needs and any custom work',
    monthlyNote: 'Ongoing support, hosting, and minor updates',
    icon: Rocket,
    badge: null,
    accent: 'from-[#1B3A87] to-[#1B3A87]',
    idealFor: 'Solo dog trainers, board-and-train specialists, and small operations that don\u2019t need kennel scheduling or a POS system.',
    includes: [
      'Custom-branded K9os web app on your own domain',
      'Dedicated board-and-train workflow (intake, agreement, daily notes)',
      'Client & dog CRM with photos, VAX records, and training history',
      'Backend admin dashboard tailored to your business',
      'Native client PWA \u2014 clients install K9os on their phone with push notifications',
      'Digital waivers & e-signed board-and-train agreements',
      'Trainer \u2194 client messenger with photo + video sharing',
      'Success timeline for public marketing wins',
      'Connected to YOUR integrated systems (Stripe / PayPal / others)',
      '24/7 one-on-one support & minor updates included in your monthly fee',
    ],
    excluded: [
      'Full kennel/boarding scheduling grid',
      'In-store POS terminal (retail hardware)',
      'Multi-role staff scheduling for large teams',
      'Regional SEO landing pages (state / country / worldwide)',
      'AI-powered SEO blog engine + AI cover-image generation',
    ],
    cta: 'Start with Dog Trainers plan',
  },
  {
    slug: 'pro',
    name: 'Professional Full-Suite',
    tagline: 'For large facilities running board & train + boarding + retail',
    setupPrice: '$2,500',
    setupSuffix: 'one-time branded set-up fee included*',
    monthlyPrice: '$250',
    monthlyLabel: '/ month',
    // Live PayPal Subscription plan ID for $250/month recurring
    paypalPlanId: 'P-88609392L88135218NJ4L7SY',
    setupNote: 'Final quote depends on scope, integrations, and any custom work',
    monthlyNote: '24/7 priority one-on-one support, hosting, ongoing updates & minor changes',
    icon: Award,
    badge: 'Most popular',
    accent: 'from-[#1B3A87] via-[#1B3A87] to-[#C8102E]',
    idealFor: 'Dog-training companies running board & train, boarding, daycare, retail, and memberships \u2014 with staff and multi-role permissions.',
    includes: [
      'Everything in the Dog Trainers plan, plus:',
      'Full kennel-run grid + trainer grid + boarding calendar',
      'Built-in POS terminal (retail + services on one Stripe/PayPal rail)',
      'Monthly VIP membership billing with automatic recurring payments',
      'Complete accounting ledger with tax-ready exports',
      'Multi-role access \u2014 Admin, Trainer, Front Desk, Clients',
      'AI-powered SEO blog engine with AI cover-image generation',
      'Regional SEO landing pages (state / country / worldwide)',
      'Priority integrations \u2014 Twilio SMS, Google Business, custom APIs',
      'White-label extensions \u2014 we build anything you need',
      '24/7 priority one-on-one support with a same-business-day response',
    ],
    excluded: [],
    cta: 'Get the Full Suite',
  },
];

const SHARED_INCLUSIONS = [
  { icon: HeadphonesIcon, label: '24/7 one-on-one support included in monthly fee' },
  { icon: ShieldCheck,     label: 'Minor updates & changes are included in the monthly fee*' },
  { icon: Zap,             label: 'Your OWN custom-built K9os app (not shared SaaS)' },
  { icon: CreditCard,      label: 'Connected to YOUR integrated systems if needed, or let us create them for you*' },
];

export default function Pricing() {
  useEffect(() => {
    document.title = 'K9os Pricing — Custom Dog Training Software from $1,000 setup + $125/mo';
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'K9os pricing for dog trainers and dog training companies. Two custom-built packages — Trainer Starter from $1,000 setup + $125/mo and Professional Full-Suite from $2,500 setup + $250/mo. Includes 24/7 support and your own custom app.');
    setMeta('keywords', 'K9os pricing, dog training software pricing, dog training software cost, K9os Trainer Starter, K9os Professional, custom dog training app pricing, board and train software pricing');
    setMeta('og:title', 'K9os Pricing — Custom Dog Training Software Packages');
    setMeta('og:description', 'Two custom-built K9os packages for dog trainers of any size. Trainer Starter from $1,000 + $125/mo, or Full-Suite from $2,500 + $250/mo. Your own app, 24/7 support.');
    setMeta('og:url', 'https://k9os.app/pricing');
  }, []);

  return (
    <div className='min-h-screen flex flex-col bg-white dark:bg-[#0B1220]' data-testid='page-pricing'>
      <PublicNav />

      {/* Hero */}
      <section className='relative py-16 sm:py-24 bg-gradient-to-br from-[#0B1220] via-[#0F1A33] to-[#1B3A87] text-white overflow-hidden'>
        <div aria-hidden='true' className='pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#C8102E]/25 blur-3xl' />
        <div aria-hidden='true' className='pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#93C5FD]/15 blur-3xl' />
        <div className='relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#F87171] mb-3'>
            <Sparkles className='w-3.5 h-3.5' /> Pricing &middot; custom-built for you
          </div>
          <h1 className='font-display text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight' data-testid='pricing-h1'>
            Every Trainer&rsquo;s Dream App &mdash; Custom Built to Your Business!
          </h1>
          <p className='mt-5 text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed'>
            Every trainer gets their OWN custom K9os app branded to their business, connected to their own systems that they need integrated &mdash; or created from scratch with our team! Pick the tier that matches your operation &mdash; both include 24/7 one-on-one help and monthly support included.
          </p>
        </div>
      </section>

      {/* Two-tier grid */}
      <section className='py-14 sm:py-20 bg-slate-50 dark:bg-slate-950' id='tiers'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='grid gap-6 md:grid-cols-3'>
            {PRICING_TIERS.map((tier) => (
              <Card
                key={tier.slug}
                className={`relative p-6 sm:p-8 border-2 ${tier.badge ? 'border-[#C8102E] shadow-2xl' : 'border-slate-200 dark:border-slate-800 shadow-md'} bg-white dark:bg-slate-900 flex flex-col`}
                data-testid={`pricing-tier-${tier.slug}`}
              >
                {tier.badge && (
                  <Badge className='absolute -top-3 left-6 bg-[#C8102E] text-white border-0 shadow-lg'>
                    <Award className='w-3 h-3 mr-1' /> {tier.badge}
                  </Badge>
                )}
                <div className='flex items-start gap-3'>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tier.accent} text-white flex items-center justify-center shrink-0`}>
                    <tier.icon className='w-6 h-6' />
                  </div>
                  <div className='flex-1'>
                    <h2 className='font-display text-2xl font-bold text-[#0B1220] dark:text-slate-50 leading-snug'>{tier.name}</h2>
                    <p className='text-sm text-slate-600 dark:text-slate-300 mt-1'>{tier.tagline}</p>
                  </div>
                </div>

                {/* Price block */}
                <div className='mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 space-y-4'>
                  <div>
                    <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400'>One-time setup</div>
                    <div className='flex items-baseline gap-2 mt-0.5'>
                      <span className='font-display text-4xl font-bold text-[#0B1220] dark:text-slate-50'>{tier.setupPrice}</span>
                    </div>
                    {tier.setupSuffix && (
                      <div className='text-xs font-semibold text-[#1B3A87] dark:text-[#93C5FD] mt-0.5'>{tier.setupSuffix}</div>
                    )}
                    <div className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>{tier.setupNote}</div>
                  </div>
                  {/* Monthly section — only rendered when the tier has a
                      recurring plan attached. The Website-only tier omits
                      monthlyPrice entirely so we hide the "Then $XX / mo"
                      block and show a one-time-cost badge instead. */}
                  {tier.monthlyPrice ? (
                    <div className='pt-4 border-t border-slate-100 dark:border-slate-800'>
                      <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400'>Then</div>
                      <div className='flex items-baseline gap-2 mt-0.5'>
                        <span className='font-display text-4xl font-bold text-[#C8102E]'>{tier.monthlyPrice}</span>
                        <span className='text-sm text-slate-600 dark:text-slate-300'>{tier.monthlyLabel}</span>
                      </div>
                      <div className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>{tier.monthlyNote}</div>
                    </div>
                  ) : (
                    <div className='pt-4 border-t border-slate-100 dark:border-slate-800'>
                      <div className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-bold uppercase tracking-wider'>
                        <Check className='w-3 h-3' /> One-time cost &mdash; no monthly fee
                      </div>
                      <p className='mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed'>
                        Add the <span className='font-semibold text-[#C8102E]'>$125/mo</span> Dog Trainers plan below anytime to unlock the full K9os backend.
                      </p>
                    </div>
                  )}
                </div>

                {/* Ideal-for */}
                <div className='mt-6 p-4 rounded-lg bg-slate-50 dark:bg-slate-950'>
                  <div className='text-[10px] uppercase tracking-wider font-bold text-[#1B3A87] dark:text-[#93C5FD] mb-1'>Best for</div>
                  <p className='text-sm text-slate-700 dark:text-slate-200'>{tier.idealFor}</p>
                </div>

                {/* Includes list */}
                <ul className='mt-5 space-y-2 flex-1' data-testid={`pricing-tier-${tier.slug}-includes`}>
                  {tier.includes.map((feat, i) => (
                    <li key={i} className='flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200'>
                      <span className='mt-0.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0'>
                        <Check className='w-3 h-3 text-white' strokeWidth={4} />
                      </span>
                      <span>{feat}</span>
                    </li>
                  ))}
                  {tier.excluded.length > 0 && tier.excluded.map((feat, i) => (
                    <li key={`x${i}`} className='flex items-start gap-2 text-sm text-slate-400 dark:text-slate-500 line-through'>
                      <span className='mt-0.5 w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center shrink-0'>
                        <span className='w-2 h-0.5 bg-slate-400 dark:bg-slate-500' />
                      </span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to='/contact'
                  className={`mt-6 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-extrabold text-base shadow-xl hover:-translate-y-0.5 transition-all tracking-tight ${tier.badge ? 'bg-[#C8102E] hover:bg-[#A50D26] text-white' : 'bg-[#1B3A87] hover:bg-[#16306F] text-white'}`}
                  data-testid={`pricing-cta-${tier.slug}`}
                >
                  {tier.cta} <ChevronRight className='w-5 h-5' />
                </Link>

                {/* Secure checkout row — one native K9os-styled pay link per
                     billing action. Native anchor buttons (no PayPal iframe SDK)
                     open PayPal's hosted checkout in a new tab so the visual
                     stays on-brand while payments still process through PayPal:
                     • SETUP FEE  → paypal.me/MiceliGreg/{amount}USD locks the
                       amount as a one-time payment.
                     • MONTHLY    → PayPal hosted subscription URL for the plan
                       ID above. Clicking it lands the buyer directly on
                       PayPal's subscription checkout for auto-recurring
                       monthly billing. Accepts all major credit / debit cards
                       + PayPal. */}
                <div className='mt-4 flex flex-col items-center gap-3' data-testid={`pricing-paypal-row-${tier.slug}`}>
                  <a
                    href={`https://paypal.me/MiceliGreg/${tier.setupPrice.replace(/[^0-9]/g, '')}USD`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-extrabold text-sm bg-[#003087] hover:bg-[#001f5c] text-white shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap tracking-tight ring-2 ring-[#003087]/20'
                    data-testid={`pricing-paypal-setup-${tier.slug}`}
                  >
                    <Lock className='w-4 h-4' /> Pay {tier.setupPrice} setup &middot; one-time
                  </a>
                  {tier.paypalPlanId && (
                    <a
                      href={`https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=${tier.paypalPlanId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-extrabold text-sm bg-[#C8102E] hover:bg-[#A50D26] text-white shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap tracking-tight ring-2 ring-[#C8102E]/25'
                      data-testid={`pricing-paypal-monthly-${tier.slug}`}
                    >
                      <Lock className='w-4 h-4' /> Start {tier.monthlyPrice}/mo subscription
                    </a>
                  )}
                </div>

                {/* Trust strip — communicates SSL + secure processing + card
                    acceptance so buyers know the checkout is legit and encrypted.
                    Intentionally does NOT name the payment processor on the
                    outside (per user request) — the processor brand only
                    reveals itself after the buyer clicks and lands on the
                    hosted checkout page. All card data lives on the PCI-DSS
                    processor; K9os never stores card data. */}
                <div className='mt-3 flex flex-col items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400'>
                  <div className='inline-flex items-center gap-1.5'>
                    <Lock className='w-3 h-3' />
                    <span>SSL-encrypted secure checkout &middot; Buyer Protection included</span>
                  </div>
                  <div className='inline-flex items-center gap-1 font-semibold text-slate-600 dark:text-slate-300'>
                    <span>VISA</span> &middot; <span>Mastercard</span> &middot; <span>AMEX</span> &middot; <span>Discover</span>
                  </div>
                </div>
                <p className='mt-2 text-[11px] text-slate-500 dark:text-slate-400 text-center leading-relaxed'>
                  <span className='font-semibold'>Setup fee</span> is a locked-amount one-time payment. <span className='font-semibold'>Monthly fee</span> is set up as an auto-recurring subscription during onboarding.
                </p>
              </Card>
            ))}
          </div>

          {/* Shared inclusions strip */}
          <div className='mt-10 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8'>
            <div className='text-center mb-6'>
              <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-2'>
                <ShieldCheck className='w-3.5 h-3.5' /> Included with every tier
              </div>
              <h3 className='font-display text-2xl font-bold text-[#0B1220] dark:text-slate-50'>What every K9os client gets</h3>
            </div>
            <div className='grid sm:grid-cols-2 md:grid-cols-4 gap-4'>
              {SHARED_INCLUSIONS.map((it, i) => (
                <div key={i} className='flex items-start gap-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-950'>
                  <div className='w-9 h-9 rounded-lg bg-[#1B3A87]/10 dark:bg-[#93C5FD]/15 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center shrink-0'>
                    <it.icon className='w-4 h-4' />
                  </div>
                  <div className='text-sm font-medium text-slate-700 dark:text-slate-200'>{it.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Custom-build promise callout */}
      <section className='py-14 sm:py-20 bg-white dark:bg-[#0B1220]'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-3'>
            <Sparkles className='w-3.5 h-3.5' /> Custom-built promise
          </div>
          <h2 className='font-display text-3xl sm:text-4xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
            No two K9os apps are the same.
          </h2>
          <p className='mt-4 text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed'>
            Unlike Gingr, Time to Pet, or Kennel Connection &mdash; where every customer gets the same shared SaaS &mdash; every K9os trainer gets their own custom-built app connected to their own Stripe or PayPal account, their own integrations (Twilio SMS, Google Business, Zapier, custom APIs), and their own workflow. White-label ready. If it&rsquo;s not on this list and you need it, we build it.
          </p>
          <Link
            to='/contact'
            className='mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all'
            data-testid='pricing-custom-cta'
          >
            Book a free scoping call <ChevronRight className='w-4 h-4' />
          </Link>
        </div>
      </section>

      {/* Lead form (same one used everywhere) */}
      <DemoLeadSection
        regionName='the World'
        regionSlug='pricing'
        eyebrow='See K9os pricing in action'
        headingOverride='Ready to lock in your K9os package?'
      />

      <MessageGregBubble />
    </div>
  );
}
